import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AdminAction {
  id: string;
  user_id: string;
  user_name: string | null;
  action: string;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

interface UseAdminAuditLogResult {
  actions: AdminAction[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  fetchActions: (actionType?: string, page?: number) => Promise<void>;
  exportToCsv: () => void;
}

export function useAdminAuditLog(): UseAdminAuditLogResult {
  const [actions, setActions] = useState<AdminAction[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const fetchActions = useCallback(async (
    actionType?: string,
    pageNum: number = 1
  ) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_action_log', {
        p_page: pageNum,
        p_page_size: pageSize,
        p_action_type: actionType || null
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table query
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          // RPC not found - fall back to direct table query (silent)

          let query = supabase
            .from('audit_logs')
            .select('id, user_id, action, details, ip_address, user_agent, created_at', { count: 'exact' })
            .order('created_at', { ascending: false })
            .range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

          if (actionType) {
            query = query.eq('action', actionType);
          }

          const { data: logsData, error: logsError, count } = await query;

          if (logsError) {
            // Table might not exist - return empty array
            // Check multiple ways a missing table error might be reported
            const isTableMissing =
              logsError.code === '42P01' ||
              logsError.code === 'PGRST204' ||
              logsError.code === 'PGRST116' ||
              logsError.message?.includes('does not exist') ||
              logsError.message?.includes('relation') ||
              (logsError as { status?: number }).status === 404 ||
              (logsError as { statusCode?: number }).statusCode === 404;

            if (isTableMissing) {
              // Silent - just return empty
              setActions([]);
              setTotal(0);
              setPage(pageNum);
              return;
            }
            throw logsError;
          }

          // Get user names from profiles
          const userIds = [...new Set((logsData || []).map(l => l.user_id).filter(Boolean))];
          let userNamesMap: Record<string, string> = {};

          if (userIds.length > 0) {
            try {
              const { data: profilesData } = await supabase
                .from('profiles')
                .select('id, full_name')
                .in('id', userIds);

              if (profilesData) {
                userNamesMap = profilesData.reduce((acc, p) => {
                  acc[p.id] = p.full_name || 'Unknown';
                  return acc;
                }, {} as Record<string, string>);
              }
            } catch {
              console.warn('Could not fetch user names');
            }
          }

          const mappedActions = (logsData || []).map(log => ({
            id: log.id,
            user_id: log.user_id,
            user_name: userNamesMap[log.user_id] || null,
            action: log.action,
            details: log.details as Record<string, unknown> | null,
            ip_address: log.ip_address,
            user_agent: log.user_agent,
            created_at: log.created_at
          })) as AdminAction[];

          setActions(mappedActions);
          setTotal(count || 0);
          setPage(pageNum);
          return;
        }
        throw error;
      }

      // RPC exists - parse result (legacy format, may need mapping)
      const result = data as unknown as { success: boolean; actions: AdminAction[]; total: number; page: number; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch audit log');
      }

      // Map RPC result to current interface if needed
      const mappedActions = (result.actions || []).map(a => ({
        id: a.id,
        user_id: (a as Record<string, unknown>).admin_id as string || a.user_id,
        user_name: (a as Record<string, unknown>).admin_name as string || a.user_name,
        action: (a as Record<string, unknown>).action_type as string || a.action,
        details: a.details || (a as Record<string, unknown>).new_state as Record<string, unknown> | null,
        ip_address: a.ip_address || null,
        user_agent: a.user_agent || null,
        created_at: a.created_at
      }));

      setActions(mappedActions);
      setTotal(result.total || 0);
      setPage(result.page || 1);
    } catch (err) {
      // Check if this is a table-not-found error before showing toast
      // Supabase errors can have various structures, so check multiple ways
      const errObj = err as { code?: string; message?: string; status?: number; statusCode?: number; hint?: string; details?: string };
      const errStr = JSON.stringify(err);
      const isTableMissing =
        errObj.code === '42P01' ||
        errObj.code === 'PGRST204' ||
        errObj.code === 'PGRST116' ||
        errObj.message?.includes('does not exist') ||
        errObj.message?.includes('relation') ||
        errObj.hint?.includes('does not exist') ||
        errObj.status === 404 ||
        errObj.statusCode === 404 ||
        errStr.includes('"code":"42P01"') ||
        errStr.includes('404') ||
        errStr.includes('does not exist');

      if (isTableMissing) {
        // Silent - table doesn't exist, just show empty
        setActions([]);
        setTotal(0);
        setPage(pageNum);
      } else {
        console.error('Failed to fetch audit log:', err);
        toast.error('Failed to fetch audit log');
      }
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const exportToCsv = useCallback(() => {
    if (actions.length === 0) {
      toast.error('No actions to export');
      return;
    }

    const headers = ['Timestamp', 'User', 'Action', 'Details', 'IP Address'];
    const rows = actions.map(action => [
      new Date(action.created_at).toISOString(),
      action.user_name || action.user_id,
      action.action,
      action.details ? JSON.stringify(action.details) : '',
      action.ip_address || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `admin-audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast.success('Audit log exported');
  }, [actions]);

  return {
    actions,
    total,
    page,
    pageSize,
    loading,
    fetchActions,
    exportToCsv
  };
}
